import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { getLocations, logLogin, getUserAttributes } from '../../utils/AWSAPI';
import '../../App.css';
import '../../assets/css/Login.css';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '../../utils/LoadingSpinner';

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setLoading] = useState(false);
  
  const loginInProgress = useRef(false);
  const hasCheckedAuth = useRef(false);

  const location = useLocation();

  const onChangeUsername = (event) => {
    setError("");
    setMessage("");
    setUsername(event.target.value);
  }

  const onChangePassword = (event) => {
    setError("");
    setMessage("");
    setPassword(event.target.value);
  }

  function fixEmail(email) {
    if (!email) return '';
    return email.toString().includes("_") ? email.split('_')[1] : email;
  }

  function isStoreAccount(email) {
     return /^\d+$/.test(email.split('@')[0]);
  }
  
  const handleLoginSuccess = useCallback(async (user) => {
    if (loginInProgress.current) {
      console.log("Login already in progress, skipping...");
      return;
    }

    loginInProgress.current = true;
    setLoading(true);
    console.log("Starting login handle for:", user);

    let userAttributes = {};
    let userIdentifier = '';
    let authMethod = 'SSO';

    try {
      console.log("Getting user attributes from API...");
      const attributeResponse = await getUserAttributes(user.username);
      
      if (attributeResponse.success) {
        userAttributes = attributeResponse.data;
      } else {
        console.error("API returned error:", attributeResponse.message);
        setError("Failed to retrieve user information. Please try again.");
        setLoading(false);
        loginInProgress.current = false;
        return;
      }
    } catch (error) {
      console.error("API call failed:", error);
      setError("Failed to retrieve user information. Please try again.");
      setLoading(false);
      loginInProgress.current = false;
      return;
    }

    console.log("Final user attributes:", userAttributes)
    
    const email = userAttributes.email;

    if(email && email.includes("@fujitsu.com")) {
      console.log("Using local account. Identifier:", email);
      userIdentifier = email;
      authMethod = "Local";
    } else {
      userIdentifier = userAttributes.EID || email || user.username;
    }

    let locationData;

    try {
      // First attempt with the original user identifier (could be either email or EID)
      locationData = await getLocations("False", userIdentifier, "0", "1");
      
      // If no location found, try adding @fujitsu.com
      if (!locationData[0]) {
        const newEmail = userIdentifier.split('@')[0] + '@fujitsu.com';
        locationData = await getLocations("False", newEmail, "0", "1");

        if (!locationData[0]) {
          setError("Warning: Your account doesn't seem to have any associated restaurants. Please contact your administrator.");
          setLoading(false);
          return loginInProgress.current = false;
        } else {
          userIdentifier = newEmail;
        }
      }

      // Log the login with the authentication method
      console.log("User Identifier: " + userIdentifier);
      //await logLogin(userIdentifier, authMethod);

      // Set local storage
      localStorage.setItem("user_identifier", userIdentifier);

      localStorage.setItem("user_username", user.username.toString());
      localStorage.setItem("user_email", userAttributes.email);
      localStorage.setItem("user_eid", userAttributes.EID);

      localStorage.setItem("user_store_account", isStoreAccount(userIdentifier));
      localStorage.setItem("user_auth_method", authMethod);
      
      setLoading(false);
      setMessage("Successfully logged in. Welcome!");
      
      // Redirect based on user type (power users have a search bar due to store amount)
      if(locationData[0]) {
        localStorage.setItem("user_power_user", locationData[0].ispowerUser);
        window.location.href = locationData[0].ispowerUser ? "/home" : "/restaurants";
      } else {
        window.location.href = "/home";
      }
      
    } catch (error) {
      console.error("Error handling login success:", error);
      setError("An error occurred while processing your login. Please try again.");
      setLoading(false);
      loginInProgress.current = false;
    }
  }, []);

  const handleAuthCode = useCallback(async () => {
    try {
      await Auth.federatedSignIn();
      const user = await Auth.currentAuthenticatedUser();
      await handleLoginSuccess(user);
    } catch (error) {
      console.error('Error handling SSO authentication:', error);
      setError('Failed to complete authentication. Please try again.');
    }
  }, [handleLoginSuccess]);

  useEffect(() => {
    if (hasCheckedAuth.current) {
      console.log("Auth already checked, skipping...");
      return;
    }

    if (location.state?.error) {
      setError(location.state.error);
    }

    const checkAuthStatus = async () => {
      try {
        const session = await Auth.currentSession();
        if (session.isValid()) {
          const user = await Auth.currentAuthenticatedUser();
          await handleLoginSuccess(user);
        }
      } catch (error) {
        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        if (authCode) {
          handleAuthCode();
        }
      } finally {
        hasCheckedAuth.current = true;
      }
    };

    checkAuthStatus();
  }, [handleLoginSuccess, handleAuthCode, location.state?.error]);
  
  const onClickLogin = async () => {
    if (loginInProgress.current) {
      console.log("Login already in progress...");
      return;
    }

    try {
      if (!username) {
        setError("Please Enter Username");
        return false;
      } else if(!password) {
        setError("Please Enter Password");
        return false;
      }
     
      let user;
      try {
        user = await Auth.signIn(username, password);
      } catch (err) {
        return setError("Invalid credentials. Please try again.");
      }
      handleLoginSuccess(user);

    } catch (error) {
      if (["UserNotFoundException", "NotAuthorizedException"].includes(error.code)) {
        setError("Invalid Username or Password");
        return false;
      }
      console.log("SignIn Error - ", error);
    }
  }

  const onClickSSOLogin = async () => {
    try {
      await Auth.federatedSignIn({
        provider: CognitoHostedUIIdentityProvider.Cognito
      });
    } catch (error) {
      console.error("SSO Sign In Error - ", error);
      setError("An error occurred during SSO login. Please try again.");
    }
  }

  return (
    <div className="login-wrapper">
      <LoadingSpinner show={isLoading} />

      <div className="login-logo flex-grow-1">
        <div className="signin-box text-center">
          <h5 className="text-dark mb-3">Welcome!</h5>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {message && (
            <div className="alert alert-success" role="alert">
              {message}
            </div>
          )}
          <div>
            <div className="form-group">
              <input
                onChange={onChangeUsername}
                value={username}
                autoComplete="off"
                className="form-control"
                placeholder="Enter Username"
              />
            </div>
            <div className="form-group">
              <input
                onChange={onChangePassword}
                value={password}
                type="password"
                className="form-control"
                placeholder="Enter Password"
              />
            </div>
            <button type='button' className="signin-btn" onClick={onClickLogin}>Login via Local Account</button>
            <hr/>
            <button type='button' className="signin-btn" onClick={onClickSSOLogin}>Login via SSO</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
